'use client';

import classNames from 'classnames';
import { usePlausible } from 'next-plausible';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';
import { useIntersection } from 'react-use';

import { CTA } from '@/lib/tracking';
import { segment } from '@/lib/tracking/segmentAnalytics';
import { getPrice } from '@/lib/util/getPrice';
import { usePricingPageContext } from '@/pricing/PricingPageContext';
import type * as Pricing from '@/pricing/types';
import { ButtonLink } from '@/ui/Button/ButtonLink';

import styles from './TableHead.module.scss';

interface Props {
    className?: string;
    onStuck?: (stuck: boolean) => void;
    options: Pricing.Option[];
}

const SIGN_UP_URL = 'https://rock.prezly.com/signup';

const HEADER_HEIGHT = 182;

export function TableHead({ className, onStuck, options }: Props) {
    const posthog = usePostHog();
    const plausible = usePlausible();
    const { billingCycle, currency } = usePricingPageContext();

    const ref = useRef<HTMLDivElement>(null);

    // @ts-expect-error Argument of type 'RefObject<HTMLDivElement | null>' is not assignable to parameter of type 'RefObject<HTMLElement>'
    // `useIntersection` types are not yet updated to new React 19 types
    const intersection = useIntersection(ref, {
        threshold: [1],
        rootMargin: `-${HEADER_HEIGHT + 1}px 0px 0px 0px`,
    });

    useEffect(() => {
        if (intersection?.intersectionRatio) {
            onStuck?.(intersection?.intersectionRatio < 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intersection?.intersectionRatio]);

    return (
        <div className={classNames(styles.header, className)} ref={ref}>
            <div className={styles.options}>
                {options.map((option) => (
                    <div key={option.id} className={styles.option}>
                        <h3 className={styles.title}>{option.display_name}</h3>
                        <p className={styles.price}>
                            <span>{getPrice(option.prices, billingCycle, currency) ?? 0}</span> per
                            user/month
                        </p>
                        <ButtonLink
                            block
                            className={styles.button}
                            href={`${SIGN_UP_URL}?plan=${option.id}`}
                            variant={option.display_name === 'core' ? 'primary' : 'secondary'}
                            size="medium"
                            onClick={() => {
                                posthog.capture(CTA.CLICKED, {
                                    href: `${SIGN_UP_URL}?plan=${option.id}`,
                                });
                                segment.track(CTA.CLICKED, {
                                    href: `${SIGN_UP_URL}?plan=${option.id}`,
                                });
                                plausible('CTAs', {
                                    props: {
                                        href: `${SIGN_UP_URL}?plan=${option.id}`,
                                        label: 'Start trial',
                                    },
                                });
                            }}
                        >
                            Start trial
                        </ButtonLink>
                    </div>
                ))}
            </div>
        </div>
    );
}
